import React from 'react';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { SectionHeader, Text, TextButtonAccordion } from 'components';
import { theme, toRem } from 'design';
import { LoadingSkeleton } from 'screens/SiteSettings/styles';
import { SalesGroupItem } from 'screens/SiteSettings/TipCalculator/components/SalesGroupItem';
import { TipShareItem } from 'screens/SiteSettings/TipCalculator/components/TipShareItem';
import { DEFAULT_POOL_NAME, IPaymentSetCalculations } from 'types';
import { formatTime, sortAlphaNumerically, toTitleCase } from 'utils';
import { IExportData } from './types';

const Layout = styled.section`
  flex-direction: column;
  width: 100%;
`;

const SettingSection = styled.section`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${toRem(5.5)};
  padding: ${toRem(16)};

  &:not(:last-child),
  > div:not(:last-child) {
    margin-bottom: ${toRem(16)};
  }
`;

const DetailsContainer = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  padding: ${toRem(16)};
`;

const DetailsListItem = styled.li`
  margin-left: ${toRem(16)};
  font-size: ${toRem(11)};
  color: ${theme.color.neutral[500]};

  span {
    color: ${theme.color.neutral[500]};
  }
`;

const DetailsRow = styled.div<{ isHeader?: boolean; tableView?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding-top: ${toRem(8)};
  width: 100%;

  & > *:first-child {
    word-break: break-word;
  }

  ${({ isHeader }) =>
    isHeader &&
    css`
      p {
        color: ${theme.color.neutral[500]};
      }
    `}

  ${({ tableView }) =>
    tableView &&
    css`
      &:not(:last-child) {
        padding-bottom: ${toRem(8)};
        border-bottom: 1px solid ${theme.color.neutral[200]};
      }
    `}
`;

interface ISettingsReportProps {
  header: (data?: IExportData) => React.ReactNode;
  calculations?: IPaymentSetCalculations;
  businessDay: string;
  isAdvanced?: boolean;
  isLoading?: boolean;
}

const SettingsReport = ({ header, calculations, businessDay, isAdvanced, isLoading }: ISettingsReportProps) => {
  const sortedPointValueRules = calculations?.results[0].rules?.filter((rule) => !rule.default).sort(sortAlphaNumerically('contributor.name'));
  const sortedTipShareRules = sortedPointValueRules.filter((rule) => !!rule.sharePolicy);
  const defaultPoolRule = calculations?.results[0].rules?.find((rule) => rule.default);

  return (
    <>
      {header()}
      <Layout>
        {isLoading ? (
          <>
            {[1, 2, 3].map((_, idx) => (
              <SettingSection key={`loading-skeleton-${idx}`}>
                <LoadingSkeleton width="100%" height={toRem(40)} />
              </SettingSection>
            ))}
          </>
        ) : (
          <>
            {/* Day Segment Settings */}
            <SettingSection data-cy="day-segment-settings">
              <SectionHeader title="Day Segments" />
              <TextButtonAccordion title={dayjs(businessDay).format('MMMM Do, YYYY')} buttonText="Segment Details">
                <DetailsContainer>
                  <DetailsRow data-cy="day-segment-header" isHeader>
                    <Text size="xsmall" fontWeight="medium">
                      Day Segment
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      Hours
                    </Text>
                  </DetailsRow>
                  {calculations?.results
                    .filter((result) => result.segment !== DEFAULT_POOL_NAME)
                    .map((result, idx) => (
                      <DetailsRow key={`${result.segmentId}_${idx}`} tableView>
                        <Text data-cy="segment-name" size="xsmall" fontWeight="medium">
                          {result.segment}
                        </Text>
                        <Text data-cy="segment-hours" size="xsmall">{`${formatTime(result.start)} - ${formatTime(result.end)}`}</Text>
                      </DetailsRow>
                    ))}
                </DetailsContainer>
              </TextButtonAccordion>
            </SettingSection>
            {/* Sales Group Settings */}
            {!!calculations?.results[0].meta?.categoryGroups.length && (
              <SettingSection data-cy="sales-group-settings">
                <SectionHeader title="Sales Groups" />
                {calculations?.results[0].meta?.categoryGroups?.sort(sortAlphaNumerically('name')).map((group, idx) => (
                  <SalesGroupItem key={`sales-group-${group.name}_${idx}`} categoryGroup={group} disabled />
                ))}
              </SettingSection>
            )}
            {/* Tip Share Settings */}
            <SettingSection data-cy="tip-share-settings">
              <SectionHeader title="Tip Shares" />
              {sortedTipShareRules.map((rule, i) => (
                <TipShareItem key={`tip-share-${i}`} tipShare={rule} isAdvanced={isAdvanced} disabled />
              ))}
            </SettingSection>
            {/* Default Pool Settings */}
            {defaultPoolRule && (
              <SettingSection data-cy="default-pool-settings">
                <SectionHeader title="Default Pool" />
                <TipShareItem tipShare={defaultPoolRule} disabled />
              </SettingSection>
            )}
            {/* Point Values Settings */}
            <SettingSection data-cy="point-values-settings">
              <SectionHeader title="Point Values" />
              <TextButtonAccordion title="Jobs and Points" buttonText="Point Values">
                <DetailsContainer>
                  <DetailsRow data-cy="job-point-values-header">
                    <Text size="xsmall" fontWeight="medium">
                      Job Title
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      Points
                    </Text>
                  </DetailsRow>
                  {sortedPointValueRules.map((rule, idx) => (
                    <DetailsListItem key={`job-point-values_${idx}`}>
                      <DetailsRow>
                        <Text data-cy="job-title" as="span" size="xsmall" fontWeight="medium">
                          {toTitleCase(rule.contributor.name)}
                        </Text>
                        <Text data-cy="job-points" as="span" size="xsmall">
                          {rule.value}
                        </Text>
                      </DetailsRow>
                    </DetailsListItem>
                  ))}
                </DetailsContainer>
              </TextButtonAccordion>
            </SettingSection>
          </>
        )}
      </Layout>
    </>
  );
};

export default SettingsReport;
